/*
 * Chuyển thực hiện — khách đã chốt: chuyển một lượt toàn bộ dịch vụ trên phiếu bác sĩ đã ký sang app thực hiện
 * (Nội khoa / Ngoại khoa / Cấy tóc theo nhóm dịch vụ). Xếp lịch, phòng, người làm do app thực hiện đảm nhận (nghiệp vụ đang BA).
 * Màn này không hiện tiền (họp 30/10): chỉ dịch vụ, tiền sử bệnh lý, yêu cầu chuyên môn.
 */
import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Button, Input, Popconfirm, Tag, message } from 'antd';
import { Send } from 'lucide-react';
import {
  DOCTORS, RESULTS, activePlans, cardSessions, cardSummary, dmhm, doctorName, quoteValid, reqLabel, uid,
  type ExecPlan, type QLine, type Session, type Visit,
} from './logic';
import { useTuVan } from './store';
import { Card, DoctorGate, FlowBar, Hint, NoVisit, NotStarted, Row, Shell, useMe, useVisit } from './shared';
import ScreeningView from './ScreeningView';
import { GROUPS, groupByKey, groupOfMang, groupOfText, useGroupCfg, type Group } from '../Telesales/lead/groups';

const PRODUCT_UNITS = ['hộp', 'tuýp', 'chai', 'lọ', 'sản phẩm'];

/** Số buổi của một dòng: thẻ liệu trình theo tổng buổi của gói (cả buổi tặng), đơn vị "buổi" theo số lượng, còn lại 1 buổi */
const countOf = (l: QLine) => (l.card ? Math.max(1, cardSessions(l)) : l.unit === 'buổi' ? Math.max(1, l.qty) : 1);

function Body({ v }: { v: Visit }) {
  const me = useMe();
  const update = useTuVan((s) => s.update);
  const cfg = useGroupCfg((s) => s.cfg);
  const q = v.quote;
  // Sản phẩm mang về (hộp, tuýp…) và thẻ trả trước (trừ tiền khi dùng dịch vụ) không chuyển thực hiện
  const lines = (q?.lines ?? []).filter((l) => !l.gift && !PRODUCT_UNITS.includes(l.unit) && l.card?.kind !== 'value');
  const prepaid = (q?.lines ?? []).filter((l) => l.card?.kind === 'value');
  const products = (q?.lines ?? []).filter((l) => PRODUCT_UNITS.includes(l.unit));
  const plans = activePlans(v);
  const [note, setNote] = useState('');

  // Dịch vụ đã chuyển: theo mã dòng; lịch cũ chỉ lưu tên thì khớp theo tên (mỗi lịch gán cho một dòng)
  const used = new Set<string>();
  const sent = new Map<string, ExecPlan>();
  lines.forEach((l) => {
    const p = plans.find((x) => !used.has(x.id) && (x.lineId ? x.lineId === l.id : x.line === l.name));
    if (p) { used.add(p.id); sent.set(l.id, p); }
  });
  const pending = lines.filter((l) => !sent.has(l.id));

  // Thẻ liệu trình xếp app theo tên gói và các dịch vụ trong gói
  const groupOf = (l: QLine): Group => groupByKey(sent.get(l.id)?.app) ?? groupByKey(groupOfText([l.name, ...(l.card?.items.map((x) => x.name) ?? [])].join(' '), cfg)) ?? groupOfMang(v.mang);
  const byGroup = GROUPS.map((g) => ({ g, items: lines.filter((l) => groupOf(l).key === g.key) })).filter((x) => x.items.length);

  const signed = quoteValid(q);
  const closed = !!v.result && RESULTS[v.result.kind].closed;
  const ready = signed && closed && pending.length > 0;
  const lastAt = plans.length ? Math.max(...plans.map((p) => p.at)) : undefined;

  const transfer = () => {
    const at = Date.now();
    const doctor = q?.exam.doctor ?? DOCTORS[0].id;
    const created: ExecPlan[] = pending.map((l) => ({
      id: uid(), at, by: me.name, line: l.name, lineId: l.id, app: groupOf(l).key, note: note.trim() || undefined,
      doctor, mode: 'first', gapDays: 0,
      sessions: Array.from({ length: countOf(l) }, (_, i): Session => ({ no: i + 1, status: 'unscheduled' })),
    }));
    const apps = Array.from(new Set(created.map((p) => groupByKey(p.app)?.label))).join(', ');
    update(v.id, (x) => ({ plans: [...created, ...x.plans] }), { text: `Chuyển thực hiện ${created.length} dịch vụ sang App ${apps}`, by: me.name });
    setNote('');
    message.success(`Đã chuyển ${created.length} dịch vụ sang App ${apps}.`);
  };
  const undo = () => {
    const at = Date.now();
    update(v.id, (x) => ({ plans: x.plans.map((p) => (p.cancelledAt ? p : { ...p, cancelledAt: at })) }), { text: 'Huỷ chuyển thực hiện', by: me.name });
  };

  return (
    <>
      <NotStarted v={v} />

      <div className="grid grid-cols-1 items-start gap-3 sm:gap-4 xl:grid-cols-3">
        <Card
          className="xl:col-span-2"
          title="Chuyển sang app thực hiện"
          extra={lastAt && !pending.length ? <Tag color="green" className="!m-0">Đã chuyển {dmhm(lastAt)}</Tag> : <span className="text-xs text-gray-500">{lines.length} dịch vụ</span>}
        >
          {!lines.length ? (
            <div className="flex flex-wrap items-center justify-between gap-2">
              <Hint>Chưa có dịch vụ — bác sĩ chỉ định ở bước Bác sĩ khám.</Hint>
              <Link to={`/bac-si-kham?v=${v.id}`} className="text-[13px] text-[#3e91ab]">Mở Bác sĩ khám</Link>
            </div>
          ) : (
            <div className="space-y-3">
              {/* Dịch vụ gom theo app nhận */}
              {byGroup.map(({ g, items }) => (
                <div key={g.key} className="rounded-xl border border-gray-200 dark:border-gray-700">
                  <div className="flex items-center justify-between gap-2 border-b border-gray-100 px-3 py-2 dark:border-gray-700">
                    <span className="flex items-center gap-2 text-[13px] font-semibold text-gray-800 dark:text-gray-100"><Tag color={g.color} className="!m-0">App {g.label}</Tag>{items.length} dịch vụ</span>
                  </div>
                  <ul className="divide-y divide-gray-100 dark:divide-gray-700">
                    {items.map((l) => {
                      const p = sent.get(l.id);
                      return (
                        <li key={l.id} className="flex items-start gap-3 px-3 py-2.5">
                          <div className="min-w-0 flex-1">
                            <div className="text-[13px] font-medium leading-snug text-gray-800 dark:text-gray-100">{l.name}</div>
                            <div className="mt-0.5 text-xs text-gray-500">{l.card ? <><Tag color="purple" className="!mr-1 !text-[10px]">Thẻ {l.card.code}</Tag>{l.qty} thẻ · {countOf(l)} buổi · {cardSummary(l.card, false)}</> : <>{l.qty} {l.unit}{l.unit === 'buổi' ? '' : ` · ${countOf(l)} buổi`}</>}</div>
                          </div>
                          {p ? <Tag color="green" className="!m-0 shrink-0">Đã chuyển {dmhm(p.at)}</Tag> : <Tag className="!m-0 shrink-0">Chưa chuyển</Tag>}
                        </li>
                      );
                    })}
                  </ul>
                </div>
              ))}
              {prepaid.length > 0 && (
                <div className="rounded-lg bg-purple-50 px-3 py-2 text-xs text-purple-800 dark:bg-purple-500/10 dark:text-purple-200">
                  Thẻ trả trước, không chuyển thực hiện (khách dùng thẻ trừ tiền khi làm dịch vụ): {prepaid.map((l) => `${l.name} × ${l.qty}`).join(' · ')}
                </div>
              )}
              {products.length > 0 && (
                <div className="rounded-lg bg-gray-50 px-3 py-2 text-xs text-gray-600 dark:bg-gray-700/40 dark:text-gray-300">
                  Sản phẩm mang về, không chuyển thực hiện: {products.map((l) => `${l.name} × ${l.qty} ${l.unit}${l.gift ? ' (tặng)' : ''}`).join(' · ')}
                </div>
              )}

              {pending.length > 0 ? (
                <div className="space-y-2 border-t border-gray-100 pt-3 dark:border-gray-700">
                  <Input.TextArea rows={2} value={note} onChange={(e) => setNote(e.target.value)} disabled={!signed || !closed} maxLength={300}
                    placeholder="Dặn bộ phận thực hiện (không bắt buộc) — VD: khách muốn làm buổi sáng, sợ đau" />
                  <div className="flex flex-wrap items-center gap-2">
                    <Popconfirm title={`Chuyển ${pending.length} dịch vụ sang app thực hiện?`} okText="Chuyển" cancelText="Huỷ" onConfirm={transfer} disabled={!ready}>
                      <Button type="primary" size="large" icon={<Send size={16} />} disabled={!ready}>
                        {plans.length ? `Chuyển nốt ${pending.length} dịch vụ` : 'Chuyển thực hiện'}
                      </Button>
                    </Popconfirm>
                    {/* Thiếu điều kiện thì nói rõ thiếu gì, kèm link sang bước cần làm */}
                    {!signed && <span className="text-xs text-amber-600">Phiếu báo giá chưa có hiệu lực (cần bác sĩ ký) · <Link to={`/phieu-bao-gia?v=${v.id}`} className="text-[#3e91ab]">Mở báo giá</Link></span>}
                    {signed && !closed && <span className="text-xs text-amber-600">{v.result ? `Kết quả “${RESULTS[v.result.kind].short}” chưa phải chốt` : 'Chưa ghi kết quả chốt'} · <Link to={`/ket-qua-tu-van?v=${v.id}`} className="text-[#3e91ab]">Ghi kết quả</Link></span>}
                  </div>
                </div>
              ) : (
                <div className="flex flex-wrap items-center justify-between gap-2 rounded-lg border border-green-200 bg-green-50 px-3 py-2.5 text-[13px] text-green-800 dark:border-green-500/30 dark:bg-green-500/10 dark:text-green-300">
                  <span>Đã chuyển toàn bộ dịch vụ — app thực hiện xếp lịch, phòng và người làm.{plans.find((p) => p.note)?.note ? ` Dặn: “${plans.find((p) => p.note)!.note}”` : ''}</span>
                  <Popconfirm title="Huỷ chuyển thực hiện?" description="Các dịch vụ quay về trạng thái chưa chuyển." okText="Huỷ chuyển" cancelText="Không" onConfirm={undo}>
                    <Button size="small" type="text" danger>Huỷ chuyển</Button>
                  </Popconfirm>
                </div>
              )}
            </div>
          )}
          <Hint>Nghiệp vụ thực hiện (xếp lịch, trừ buổi, xác nhận đã làm) đang được BA — màn này chỉ chuyển dịch vụ sang đúng app.</Hint>
        </Card>

        {/* Thông tin cho bộ phận chuyên môn */}
        <div className="flex flex-col gap-3 sm:gap-4">
          <Card title="Thông tin chuyên môn">
            <div className="space-y-2">
              <Row label="Bác sĩ khám">{q?.exam.signedAt ? `${doctorName(q.exam.doctor)}${q.exam.level ? ` · ${q.exam.level}` : ''}${q.exam.area ? ` · ${q.exam.area}` : ''}` : '—'}</Row>
              <Row label="Yêu cầu chuyên môn">{q?.exam.reqs?.length ? q.exam.reqs.map((r) => <Tag key={r} color="red" className="!mb-1">{reqLabel(r)}</Tag>) : 'Không có'}</Row>
              {q?.exam.note && <Row label="Bác sĩ ghi">{q.exam.note}</Row>}
              {q?.exam.sign && <Row label="Chữ ký"><img src={q.exam.sign} alt={`Chữ ký ${doctorName(q.exam.doctor)}`} className="h-12 rounded border border-gray-200 bg-white" /></Row>}
            </div>
            <Hint>Màn chuyển thực hiện không hiện giá. Dịch vụ thuộc nhóm nào thì chuyển về app chuyên môn của nhóm đó.</Hint>
          </Card>
          <ScreeningView v={v} compact title="Tiền sử bệnh lý (khách khai)" />
        </div>
      </div>
    </>
  );
}

export default function TransferExecutionPage() {
  const { v } = useVisit();
  return (
    <Shell title="Chuyển thực hiện" sub="Khách đã chốt: chuyển một lượt dịch vụ sang app thực hiện theo nhóm">
      <FlowBar v={v} step="exec" />
      {v ? <DoctorGate v={v}><Body key={v.id} v={v} /></DoctorGate> : <NoVisit />}
    </Shell>
  );
}
